
import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppContext } from '../App.jsx';
import { ArrowLeft, Bell, Star, Trash2, CheckCircle2, Copy, Wallet, CheckCheck } from 'lucide-react';
import AuthLoadingScreen from './AuthLoadingScreen.jsx';
import { doc, updateDoc } from 'firebase/firestore';
import ConfirmationModal from '../components/common/ConfirmationModal';

const NotificationItem = ({ notification, onDelete }) => {
    const isRatingRequest = notification.type === 'rating_request_servi' || notification.type === 'rating_request_client';
    const isImportant = notification.importance === 'high';
    const isUnread = notification.status === 'pending';

    const dateDisplay = useMemo(() => {
        if (!notification.createdAt) return 'Agora';
        const date = notification.createdAt.toDate ? notification.createdAt.toDate() : new Date();
        return date.toLocaleDateString('pt-BR') + ' às ' + date.toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'});
    }, [notification.createdAt]);

    const content = (
        <div className={`p-4 rounded-3xl shadow-sm border flex items-start space-x-4 w-full transition-all active:scale-[0.99] ${isUnread ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-100 opacity-80'}`}>
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${isRatingRequest ? 'bg-yellow-100' : (isImportant ? 'bg-red-100' : 'bg-blue-100')}`}>
                {isRatingRequest
                    ? <Star className="w-5 h-5 text-yellow-500" />
                    : <Bell className={`w-5 h-5 ${isImportant ? 'text-red-500' : 'text-blue-600'}`} />
                }
            </div>
            <div className="flex-grow min-w-0 text-left">
                <div className='flex justify-between items-start'>
                    <h3 className={`font-bold text-sm truncate pr-2 ${isUnread ? 'text-blue-900' : 'text-gray-800'}`}>{notification.title}</h3>
                    <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(notification.id); }}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
                {notification.messagePreview && (
                    <p className="text-xs text-gray-500 line-clamp-2 mt-0.5 leading-relaxed">{notification.messagePreview}</p>
                )}
                <p className="text-[9px] text-gray-400 mt-2 uppercase font-black tracking-widest">{dateDisplay}</p>
            </div>
        </div>
    );

    if (isRatingRequest) {
        return (
            <Link to="/avaliar" state={{ notification: notification }} className="w-full block">
                {content}
            </Link>
        );
    }

    return content;
};

const NotificationsScreen = () => {
    const navigate = useNavigate();
    const { db, notifications, activeRole, showMessage, isLoading } = useAppContext();
    const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);

    const pixKey = "9598f95f-0ec4-48c6-9604-94fde76d8e41";

    const hasPaymentNotif = useMemo(() => {
        return notifications.some(n => n.type === 'payment_support' && n.status === 'pending');
    }, [notifications]);

    const filteredNotifications = useMemo(() => {
        if (!activeRole) return [];
        return notifications.filter(notif => notif.targetRole === activeRole && notif.status !== 'deleted');
    }, [notifications, activeRole]);

    const unreadCount = useMemo(() => {
        return filteredNotifications.filter(n => n.status === 'pending').length;
    }, [filteredNotifications]);

    // FUNÇÃO CIRÚRGICA: Marcar todas como lidas manualmente
    const handleMarkAllAsRead = async () => {
        const unread = filteredNotifications.filter(n => n.status === 'pending');
        if (unread.length === 0 || !db) return;
        try {
            await Promise.all(unread.map(n =>
                updateDoc(doc(db, 'notifications', n.id), { status: 'done' })
            ));
            showMessage("Todas marcadas como lidas!", "success");
        } catch (error) {
            showMessage("Erro ao atualizar status.", "error");
        }
    };

    const copyPix = () => {
        navigator.clipboard.writeText(pixKey);
        showMessage("Chave PIX copiada!", "success");
    };

    const handleDeleteNotification = async (id) => {
        if (!db) return;
        try {
            await updateDoc(doc(db, 'notifications', id), { status: 'deleted' });
        } catch (error) {
            console.error("Erro ao excluir:", error);
        }
    };

    const handleDeleteAll = async () => {
        if (!db) return;
        try {
            await Promise.all(filteredNotifications.map(n =>
                updateDoc(doc(db, 'notifications', n.id), { status: 'deleted' })
            ));
            setIsDeleteAllModalOpen(false);
        } catch (error) {
            console.error("Erro ao limpar tudo:", error);
        }
    };

    if (isLoading) return <AuthLoadingScreen message="Sincronizando alertas..." />;

    return (
        <div className="p-4 h-full flex flex-col bg-gray-50 pb-24 text-left">
            <div className="flex items-center justify-between mb-6 px-1">
                <div className='flex items-center'>
                    <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200 mr-2 active:scale-90 transition-all">
                        <ArrowLeft className="w-6 h-6 text-gray-700" />
                    </button>
                    <h2 className="text-xl font-black text-gray-800 uppercase tracking-tighter">Notificações</h2>
                </div>

                <div className="flex items-center space-x-2">
                    {unreadCount > 0 && (
                        <button
                            onClick={handleMarkAllAsRead}
                            className="flex items-center p-2.5 text-blue-600 bg-white border border-blue-50 rounded-xl shadow-sm active:scale-95 transition-all"
                            title="Marcar todas como lidas"
                        >
                            <CheckCheck size={18} />
                        </button>
                    )}
                    {filteredNotifications.length > 0 && (
                        <button
                            onClick={() => setIsDeleteAllModalOpen(true)}
                            className="p-2.5 text-red-500 bg-white border border-red-50 rounded-xl shadow-sm active:scale-95 transition-all"
                            title="Limpar tudo"
                        >
                            <Trash2 size={18} />
                        </button>
                    )}
                </div>
            </div>

            <div className="space-y-3 flex-grow overflow-y-auto no-scrollbar">
                {activeRole === 'servi' && hasPaymentNotif && (
                    <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 rounded-[2.5rem] text-white shadow-xl mb-6 relative overflow-hidden animate-slide-up">
                        <div className="relative z-10">
                            <div className="flex items-center space-x-2 mb-3">
                                <Wallet size={20} className="text-blue-200" />
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80">Repasse de Apoio Beta</span>
                            </div>
                            <p className="text-xs font-medium leading-relaxed mb-4">
                                Identificamos contribuições pendentes referentes a serviços concluídos. O repasse de apoio ajuda a manter e evoluir o T-SERVI durante a fase beta.
                            </p>
                            <div className="bg-white/10 border border-white/20 p-4 rounded-2xl flex items-center justify-between">
                                <div className="min-w-0">
                                    <p className="text-[9px] font-black uppercase opacity-60 mb-1">Chave PIX (PicPay)</p>
                                    <p className="text-xs font-mono font-bold truncate">{pixKey}</p>
                                </div>
                                <button onClick={copyPix} className="p-3 bg-white text-blue-700 rounded-xl active:scale-90 transition-all shadow-lg">
                                    <Copy size={16} />
                                </button>
                            </div>
                        </div>
                        <CheckCircle2 className="absolute -right-4 -bottom-4 w-32 h-32 text-white opacity-10 -rotate-12" />
                    </div>
                )}

                {filteredNotifications.length > 0 ? (
                    filteredNotifications.map(notif => (
                        <NotificationItem
                            key={notif.id}
                            notification={notif}
                            onDelete={handleDeleteNotification}
                        />
                    ))
                ) : (
                    <div className="text-center py-24 bg-white rounded-[2.5rem] shadow-sm border border-gray-100 px-8">
                        <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                        </div>
                        <p className="font-black text-gray-800 uppercase tracking-tight text-lg leading-tight text-center w-full">Tudo em dia!</p>
                        <p className="text-xs text-gray-400 font-bold mt-2 leading-relaxed text-center">Você não possui novos alertas no momento.</p>
                    </div>
                )}
            </div>

            {isDeleteAllModalOpen && (
                <ConfirmationModal
                    title="Limpar Histórico"
                    message="Deseja apagar todas as notificações deste perfil? Esta ação não pode ser desfeita."
                    confirmText="Sim, Apagar Tudo"
                    onConfirm={handleDeleteAll}
                    onClose={() => setIsDeleteAllModalOpen(false)}
                />
            )}
        </div>
    );
};

export default NotificationsScreen;
